import { showErrorSnackbar, showSuccessSnackbar } from '@doopage/react-ui-kit';
import { t } from '@lingui/macro';
import {
  getPaymentChannelBanks,
  getPaymentProviders,
  resumePaymentLink,
  startPaymentLink,
} from 'api/api';
import { handleApi } from 'helper/helper';
import { useEffect, useState } from 'react';
import { getAllChannels } from 'redux/action/apiAction';
import Routes from 'router/routes';
import DV from 'variables/DV';

export const MANUAL_BANK = 'manual_bank';
export const LINKED_BANK = 'linked_bank';

// What the backend callback appends to <dashboard>/channel after a `redirect` step
export const PAYMENT_LINK_REDIRECT_ACTION = 'payment_link';
const PAYMENT_LINK_PARAMS = [
  'redirect_action',
  'success',
  'session',
  'subtype',
  'channel_id',
  'error',
];

// Banks print the account holder name without diacritics
export const toTransferContent = (text = '') =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯˆ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, '');

// Static lists, fetched once per page load
const cache = {};
const useCachedList = (key, api) => {
  const [state, setState] = useState(
    cache[key] ? { data: cache[key], loading: false } : { data: [], loading: true },
  );

  useEffect(() => {
    if (cache[key]) return;
    let cancelled = false;
    handleApi(api()).then(({ success, data, error }) => {
      if (success) cache[key] = Array.isArray(data) ? data : [];
      if (!cancelled) setState({ data: cache[key] || [], loading: false, error });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};

// { code, bin, short_name, full_name, logo_url, is_support_link }
export const usePaymentChannelBanks = () => useCachedList('banks', getPaymentChannelBanks);

// { subtype, name, description, logo_url, signup_url }
export const usePaymentProviders = () => useCachedList('providers', getPaymentProviders);

// ── Payment link session ──────────────────────────────────────────────────────
// Drives a link session (POST payments/link) on the dashboard:
//   redirect → the provider page in this tab, back through <dashboard>/channel?redirect_action=payment_link
//   select   → SelectPaymentLinkAccountModal
//   done     → channel list refreshed
// `replace`: called from a modal, which the next modal takes the place of
export const handlePaymentLinkProgress = (progress, { history, dispatch, replace = false }) => {
  const { purpose, step } = progress || {};
  const isRelink = purpose === 'relink';
  const isUnlink = purpose === 'unlink';

  const showLinkError = (message) => {
    const title = isUnlink
      ? t`Xoá kênh thanh toán thất bại`
      : isRelink
      ? t`Cấp quyền lại kênh thanh toán thất bại`
      : t`Thêm kênh thanh toán thất bại`;
    showErrorSnackbar(message ? title + ': ' + message : title);
  };

  if (!step) {
    showSuccessSnackbar(
      isUnlink
        ? t`Đã xoá kênh thanh toán`
        : isRelink
        ? t`Cấp quyền lại kênh thanh toán thành công`
        : t`Thêm kênh thanh toán thành công`,
    );
    dispatch(getAllChannels());
    return;
  }

  if (step.kind === 'select') {
    history[replace ? 'replace' : 'push']({
      pathname: Routes.channel.payment.selectAccount.path,
      state: { isModal: true, progress },
    });
    return;
  }

  if (step.kind === 'redirect' && step.url) {
    // The provider sends the owner back through our callback, which lands on the channel page
    window.location.href = step.url;
    return;
  }

  // form / embed / otp: no provider offered on the dashboard needs them yet
  showLinkError(t`Bước liên kết chưa được hỗ trợ`);
};

// Re-authorizes the provider a linked payment channel reports through (e.g. SePay)
export const relinkPaymentChannel = async (channel, { history, dispatch }) => {
  const { success, data, error } = await handleApi(
    startPaymentLink({ subtype: channel.channel_subtype, channel_id: channel.id }),
  );
  if (!success) {
    showErrorSnackbar(t`Cấp quyền lại kênh thanh toán thất bại` + (error ? ': ' + error : ''));
    return;
  }
  handlePaymentLinkProgress(data, { history, dispatch });
};

// Called by the Loading screen with the params the callback redirected with: keeps the result
// for the channel page and drops it from the params carried along, so a reload doesn't resume
// the session again
export const stashPaymentLinkRedirect = (params) => {
  if (params.redirect_action !== PAYMENT_LINK_REDIRECT_ACTION) return;
  DV.paymentLinkRedirect = {
    success: params.success === 'true',
    session: params.session,
    error: params.error,
  };
  PAYMENT_LINK_PARAMS.forEach((key) => delete DV.params[key]);
};

// Picks the session back up once the owner is back on the channel page
export const resumePaymentLinkRedirect = async ({ history, dispatch }) => {
  const redirect = DV.paymentLinkRedirect;
  if (!redirect) return;
  DV.paymentLinkRedirect = null;

  if (!redirect.success || !redirect.session) {
    showErrorSnackbar(
      t`Liên kết kênh thanh toán thất bại` + (redirect.error ? ': ' + redirect.error : ''),
    );
    return;
  }

  const { success, data, error } = await handleApi(resumePaymentLink(redirect.session));
  if (!success) {
    showErrorSnackbar(t`Liên kết kênh thanh toán thất bại` + (error ? ': ' + error : ''));
    return;
  }
  handlePaymentLinkProgress(data, { history, dispatch });
};
